import { clamp } from '@/core/rng';
import type { Region } from './region';
import { EarthStore, ampForVolume, lensVolume } from './earth';

/**
 * What the land is already doing — and what a god gets for helping.
 *
 * `world/earth.ts` lets a god take hold of the ground and move it anywhere,
 * at full price. This is the other half Griffin asked for: **Strengthen and
 * Weaken on geological processes**, which is law 28 — *godhood can accelerate
 * causality, it cannot escape it* — applied to rock and water.
 *
 * The distinction between the two is the whole point. Grasp is arbitrary and
 * expensive: you may put a hill anywhere, and you pay for every cubic metre.
 * This is neither. You may only make the land do **more of what it was
 * already doing**, and where it is doing it hard you get it at a fraction of
 * the price. A god that understands its valley moves mountains cheaply; a god
 * that does not is reduced to digging.
 *
 * ## The process is a pure function of the baseline
 *
 * This is what makes the whole thing cost nothing to know. The region already
 * holds D8 drainage accumulation (`flowAt`) and its heights are already the
 * output of seven rounds of `incise()` and `deposit()` at worldgen — the
 * island *is* an eroded landscape. So "what process runs here" needs no state
 * at all: it is read from slope and flow, both pure functions of position.
 *
 * Only the **acceleration** is history, and that writes into `EarthStore` as
 * the same lenses a Grasp writes. One store, two ways in.
 *
 *     incise   a watercourse cutting down          channel × steep
 *     slump    a hillside shedding downhill        hillslope × steep^1.5
 *     deposit  a slack reach laying material out   channel × flat
 *     stable   nothing much is happening here
 *
 * ## Both verbs, and neither is moral
 *
 * **Strengthen** lets the process run: the channel cuts deeper, the slope
 * sheds, the flat builds up. **Weaken** holds it back — and because there is
 * no ongoing erosion loop to slow down, holding back a process means undoing
 * what it has done: filling an incision, holding a slope up against its own
 * angle. Both move real earth and both conserve it (law 22: morality is
 * subject, context and consequence, never the verb).
 *
 * ## What it costs
 *
 * Effect scales with the rate and cost falls with it, so they compound
 * exactly as `processViability` and `environmentFactor` compound for growth —
 * the 6.8× spread §15 measured, arriving again for the ground.
 *
 * Pure by law 7: no three, no `Math.random`, no clock. What the land is
 * already doing is a function of where you are standing.
 */

export type GeoProcess = 'incise' | 'slump' | 'deposit' | 'stable';

export interface GeoReading {
  process: GeoProcess;
  /** 0..1 — how hard the land is already doing it. */
  rate: number;
  /** Degrees. */
  slopeDeg: number;
  /** 0..1 — how much of a watercourse this is. */
  channel: number;
  /** Unit vector pointing downhill. Zero-length on dead-flat ground. */
  fall: { x: number; z: number };
}

/**
 * Thresholds, taken from the island's own distributions rather than taste
 * (sampled over 34,507 land points): `log1p(flow)` runs p50 1.35, p90 3.57,
 * p99 6.49; slope runs p50 12.3°, p90 43.3°.
 *
 * So `channel` opens at about the 75th percentile of flow and saturates in
 * the top ~2% — real watercourses, not damp ground — and `steep` opens just
 * under the median slope and saturates near the 90th.
 */
const CHANNEL_LO = 2.5, CHANNEL_HI = 6.0;
const STEEP_LO = 10, STEEP_HI = 40;
const FLAT_FULL = 8;

/** How far downhill removed material is carried before it settles. */
export const TRANSPORT = 140;

/**
 * What the land is doing here *now* — baseline plus whatever has been moved.
 *
 * Reading the baseline alone was the first version and it left a real hole: a
 * god could hammer the same slumping slope for ever at a third of the price,
 * because the reading never noticed the slope had gone. Sampling the *current*
 * surface closes it as physics rather than as a rule — mass wasting stops when
 * there is no longer a slope to waste, an incision stops paying once the
 * channel is cut, and the discount evaporates with the process that earned it.
 *
 * That also makes the verb self-limiting in the way law 19 wants: acting on
 * something changes the conditions of acting on it again.
 */
export function readGeology(
  region: Region, x: number, z: number, earth?: EarthStore,
): GeoReading {
  // The gradient is measured over the distance the process actually carries
  // material, not over an arbitrary 40 m. That is not a detail: a lens is
  // radially symmetric, so its gradient at its OWN CENTRE is zero — sampling
  // close in meant a god could dig the same slope for ever and the reading
  // never noticed the slope had gone. Measuring at the transport scale means
  // the pit here and the mound 140 m downhill both land inside the stencil,
  // so the slope they have removed between them is what the next act reads.
  const D = TRANSPORT / 2;
  const h = (px: number, pz: number) =>
    region.heightAt(px, pz) + (earth ? earth.at(px, pz) : 0);
  const gx = h(x + D, z) - h(x - D, z);
  const gz = h(x, z + D) - h(x, z - D);
  const grade = Math.hypot(gx, gz) / (2 * D);
  const slopeDeg = (Math.atan(grade) * 180) / Math.PI;

  const channel = clamp(
    (Math.log1p(region.flowAt(x, z)) - CHANNEL_LO) / (CHANNEL_HI - CHANNEL_LO), 0, 1);
  const steep = clamp((slopeDeg - STEEP_LO) / (STEEP_HI - STEEP_LO), 0, 1);
  const flat = clamp((FLAT_FULL - slopeDeg) / FLAT_FULL, 0, 1);

  const incise = channel * steep;
  const deposit = channel * flat;
  const slump = (1 - channel) * Math.pow(steep, 1.5);

  // the fall line, normalised — material goes where water goes
  const len = Math.hypot(gx, gz) || 1;
  const fall = { x: -gx / len, z: -gz / len };

  let process: GeoProcess = 'stable';
  let rate = 0;
  if (incise >= slump && incise >= deposit) { process = 'incise'; rate = incise; }
  if (slump > incise && slump >= deposit) { process = 'slump'; rate = slump; }
  if (deposit > incise && deposit > slump) { process = 'deposit'; rate = deposit; }
  if (rate < 0.02) { process = 'stable'; rate = 0; }

  return { process, rate, slopeDeg, channel, fall };
}

/**
 * What a god gets for its power when it works with the land instead of
 * against it. At full rate a cubic metre costs **0.3** of what Grasp charges;
 * where nothing is happening it costs full price and moves almost nothing,
 * which is the same lesson `VIABILITY_FLOOR` teaches for growth: a quarter
 * effect at full price is a lesson, and zero would be a wall.
 */
export const GEO_DISCOUNT = 0.7;
export const geoCostFactor = (rate: number): number => 1 - GEO_DISCOUNT * clamp(rate, 0, 1);

/**
 * How much earth one act of shaping moves, before the rate scales it. Read as
 * "a god at full focus, on ground doing this as hard as ground does it,
 * shifts this much" — measured against `tools/erosion.ts` so a valley deepens
 * by something a player can see in one act rather than in forty.
 */
export const GEO_DEPTH_AT_FULL_RATE = 6;

export interface ShapeResult {
  ok: boolean;
  reason?: 'nothing-happening' | 'too-weak' | 'not-land';
  process?: GeoProcess;
  rate?: number;
  /** Cubic metres actually moved. */
  volume?: number;
  cost?: number;
  /** Metres the ground fell (negative) or rose at the acted point. */
  moved?: number;
}

/**
 * Accelerate (`strengthen`) or hold back (`weaken`) whatever the land is
 * doing here, moving real earth either way.
 *
 * Returns what happened rather than doing the charging — the Simulation owns
 * the god's purse, exactly as it does for `moveEarth`.
 */
export function shapeLand(
  region: Region,
  earth: EarthStore,
  x: number, z: number,
  radius: number,
  verb: 'strengthen' | 'weaken',
  /** 0..1 — how much of the act's full strength this is. */
  strength = 1,
): { reading: GeoReading; from: { x: number; z: number; r: number; depth: number };
     to: { x: number; z: number; r: number }; volume: number; costFactor: number } | null {
  const reading = readGeology(region, x, z, earth);
  if (reading.rate <= 0) return null;

  const depth = GEO_DEPTH_AT_FULL_RATE * reading.rate * clamp(strength, 0, 1);
  // The pair straddles the acted point by half the transport distance each
  // way, so a lens narrower than that would leave a ridge of untouched ground
  // between the cut and the fill — a step where the process should be smooth.
  // The floor makes the two overlap instead.
  const r = Math.max(TRANSPORT * 0.62, radius);

  // Where the two ends of the journey are, and this is the part that took
  // three tries to get right.
  //
  // A lens is radially symmetric, so its gradient at its OWN CENTRE is zero.
  // Putting the pit on the acted point therefore left the slope there exactly
  // as steep as it had been, and a god could dig the same hillside for ever
  // at a third of the price — the probe printed nine identical rows before
  // this was believed. It is also just wrong: mass wasting takes material off
  // the UPPER slope and lays it on the LOWER one. So an erosional pair
  // **straddles** the acted point, half the transport distance either way,
  // which both gentles the slope for real and puts the two lens centres
  // exactly on the stencil the next reading samples.
  //
  // Deposition is the receiving end of somebody else's erosion, so it draws
  // from upstream and lays the material here.
  const forwards = verb === 'strengthen';
  const depositing = reading.process === 'deposit';
  const half = TRANSPORT / 2;
  const up = { x: x - reading.fall.x * half, z: z - reading.fall.z * half };
  const down = { x: x + reading.fall.x * half, z: z + reading.fall.z * half };

  const source = depositing
    ? { x: x - reading.fall.x * TRANSPORT, z: z - reading.fall.z * TRANSPORT, r: r * 1.25 }
    : { ...up, r };
  const sink = depositing ? { x, z, r } : { ...down, r: r * 1.15 };

  // The volume is a property of the PROCESS, not of which way it is being
  // run — otherwise Weaken moves more earth than Strengthen simply because a
  // depositional fan is drawn wider than the scar it came from, and law 22's
  // "neither verb is moral" quietly stops being true in the arithmetic.
  // So: solve the volume once from the erosional footprint, then solve
  // whichever end is the source to hold exactly that.
  const volume = Math.abs(lensVolume(r, depth));
  const src = forwards ? source : sink;
  const dst = forwards ? sink : source;
  const from = { ...src, depth: ampForVolume(src.r, volume) };
  const to = dst;

  return { reading, from, to, volume, costFactor: geoCostFactor(reading.rate) };
}

/**
 * Apply a plan from `shapeLand` to the store. `moved` is what happened to the
 * ground at the point that was acted on — negative if it fell.
 */
export const applyShape = (
  earth: EarthStore,
  plan: NonNullable<ReturnType<typeof shapeLand>>,
  x: number, z: number,
): { dirty: string[]; moved: number } => {
  const before = earth.at(x, z);
  const { dirty } = earth.move(plan.from, plan.to);
  return { dirty, moved: earth.at(x, z) - before };
};

/** Exported for the probe: the volume a given radius and depth holds. */
export { lensVolume, ampForVolume };
